if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        // 初始化主媒体区域的导航按钮
        this.initGalleryNavButtons();
        
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }
      
      initGalleryNavButtons() {
        const prevButton = this.querySelector('.gallery-nav-button--prev');
        const nextButton = this.querySelector('.gallery-nav-button--next');
        
        if (prevButton) {
          prevButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.navigateToPrev();
          });
        }
        
        if (nextButton) {
          nextButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.navigateToNext();
          });
        }
      }
      
      getMediaItems() {
        // 获取主媒体列表中的所有媒体项（slider__slide 类的 li 元素）
        const slider = this.elements.viewer.querySelector('.product__media-list');
        if (!slider) return [];
        return Array.from(slider.querySelectorAll(':scope > .product__media-item[data-media-id]'));
      }
      
      getCurrentIndex() {
        const mediaItems = this.getMediaItems();
        const activeItem = mediaItems.find(item => item.classList.contains('is-active'));
        return activeItem ? mediaItems.indexOf(activeItem) : 0;
      }
      
      navigateToPrev() {
        const mediaItems = this.getMediaItems();
        if (mediaItems.length <= 1) return;
        
        const currentIndex = this.getCurrentIndex();
        const newIndex = currentIndex <= 0 ? mediaItems.length - 1 : currentIndex - 1;
        
        this.activateMediaByIndex(mediaItems, newIndex);
      }
      
      navigateToNext() {
        const mediaItems = this.getMediaItems();
        if (mediaItems.length <= 1) return;
        
        const currentIndex = this.getCurrentIndex();
        const newIndex = currentIndex >= mediaItems.length - 1 ? 0 : currentIndex + 1;
        
        this.activateMediaByIndex(mediaItems, newIndex);
      }
      
      activateMediaByIndex(mediaItems, index) {
        const targetMedia = mediaItems[index];
        if (!targetMedia) return;
        
        // 移除所有 is-active 类
        mediaItems.forEach(item => item.classList.remove('is-active'));
        
        // 添加 is-active 到目标元素
        targetMedia.classList.add('is-active');
        
        // 更新缩略图状态
        if (this.elements.thumbnails) {
          const mediaId = targetMedia.dataset.mediaId;
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
        }
        
        // 播放媒体（如果是视频）
        this.playActiveMedia(targetMedia);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
